import { UseQueryReturnType } from "@tanstack/vue-query";
import { computed, ComputedRef, Ref, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { GroupByLevel, ProjectMetadata } from "../../lib/dynamodb/types";
import { type Event } from "../../lib/dynamodb/types";
import { validateTimeRange } from "../../lib/dynamodb/utils";
import type { useAvailableCameras } from "./useAvailableCameras";
import { endOfDay, startOfDay } from "./useDateHelpers";
import type { CameraWithSchedule } from "./useEventCameras";
import type { Sector } from "./useEventSectors";
import { useReactiveMidnight, useReactiveNow } from "./useReactiveTime";

export interface FilterOption {
	label: string;
	value: string;
}

export interface UseFiltersReturn {
	selectedProjectId: ComputedRef<string | undefined>;
	selectedEventId: ComputedRef<string | undefined>;
	selectedProjectName: ComputedRef<string | undefined>;
	selectedEventName: ComputedRef<string | undefined>;
	selectedCameras: Ref<string[]>;      // contains scheduleIds or "sector:xxx" entries
	selectedSectors: Ref<string[]>;      // selected sector_ids (driven from URL on stats)
	selectedCamIds: ComputedRef<string[]>; // unique cam_ids derived from expanded selection
	expandedScheduleIds: ComputedRef<string[]>; // sector entries expanded to member scheduleIds
	schedules: ComputedRef<CameraWithSchedule[]>;
	selectedDateRange: Ref<[Date, Date] | null>;
	selectedClass: Ref<string>;
	selectedGranularity: Ref<string>;
	selectedZone: Ref<string>;
	selectedDetail: Ref<string>;
	selectedGroupBy: Ref<GroupByLevel>;
	classOptions: ComputedRef<FilterOption[]>;
	cameraOptions: ComputedRef<{
		label: string;
		value: string;
	}[]>;
	eventIsLive: ComputedRef<boolean>;
	minDate: ComputedRef<Date | undefined>;
	maxDate: ComputedRef<Date | undefined>;
	eventsQuery: UseQueryReturnType<Event[], unknown>;
}

const GRANULARITY_ORDER = ["minute", "hour", "day"] as const;

const ALL_GRANULARITIES: FilterOption[] = [
	{ label: "Minute", value: "minute" },
	{ label: "Hour", value: "hour" },
	{ label: "Day", value: "day" },
];

const URL_UPDATE_DEBOUNCE_MS = 200;

function parseDateRange(urlStr: string | null): [Date, Date] | null {
	if (!urlStr) return null;
	try {
		const [startStr, endStr] = urlStr.split(",");
		const start = startOfDay(new Date(parseInt(startStr)));
		const end = endOfDay(new Date(parseInt(endStr)));
		if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
		return [start, end];
	} catch {
		return null;
	}
}

function dateRangeToUrlString(range: [Date, Date] | null): string {
	if (!range || range.length !== 2 || !range[0]) return "";
	const end = range[1] || range[0];
	return `${range[0].getTime()},${(end as Date).getTime()}`;
}

function findSmallestValidGranularity(startSeconds: number, endSeconds: number): string {
	for (const granularity of GRANULARITY_ORDER) {
		try {
			validateTimeRange(startSeconds, endSeconds, granularity);
			return granularity;
		} catch {
			continue;
		}
	}
	return "year";
}

/**
 * Compute a default date range for an event (full event range).
 */
function computeEventDateRange(event: Event): [Date, Date] {
	const start = new Date(event.event_start * 1000);
	start.setHours(0, 0, 0, 0);
	const end = new Date(event.event_end * 1000);
	end.setHours(23, 59, 59, 999);
	return [start, end];
}

export function useFilters(
	projectId: ComputedRef<string | undefined>,
	eventId: ComputedRef<string | undefined>,
	classes: Ref<string[]>,
	projectMetadataQuery: { data: { value: ProjectMetadata | null | undefined } },
	eventsQuery: UseQueryReturnType<Event[], unknown>,
	eventCameraIDsQuery: UseQueryReturnType<CameraWithSchedule[], unknown>,
	allCamerasQuery: ReturnType<typeof useAvailableCameras>,
	sectorsQuery?: UseQueryReturnType<Sector[], unknown>,
): UseFiltersReturn {
	const route = useRoute();
	const router = useRouter();

	// Route name that writes URL params. EventLive stays paramless — readers
	// only round-trip on the stats page.
	const URL_OWNER_ROUTE = "event-stats";

	const queryStr = (key: string): string | null => {
		const v = route.query[key];
		if (Array.isArray(v)) return v[0] ?? null;
		return typeof v === "string" ? v : null;
	};

	// --- URL param parsing (only used for initial load) ---
	// Stats URL schema: date_range, granularity, sectors. Cameras are no
	// longer round-tripped — the stats page filters by sector, not by cam.
	const urlSectors = queryStr("sectors");
	const urlDateRange = queryStr("date_range");
	const urlGranularity = queryStr("granularity") || "hour";

	// --- Initial load tracking ---
	// On page reload, eventId may start as undefined and resolve async
	// (e.g. from route params). This causes the eventId watcher to fire
	// as if it were an event switch (undefined → "some-id"), which would
	// blow away all URL-restored state (cameras, realTime, dateRange, etc).
	// This flag prevents that — we treat the first eventId resolution as
	// "initial load" and preserve URL state.
	let initialLoadComplete = false;

	// --- Reactive time primitives ---
	const now = useReactiveNow();
	const todayMidnight = useReactiveMidnight();

	// --- Derived project/event metadata ---
	const selectedProjectName = computed(() => projectMetadataQuery?.data?.value?.project_name);
	const selectedEventName = computed(() =>
		eventsQuery?.data?.value?.find(x => x.event_id === eventId.value)?.event_name
	);

	// --- Camera options & selection ---
	// Flat list of schedules — sectors are a map-only concept
	const cameraOptions = computed(() => {
		const eventCams = eventCameraIDsQuery?.data?.value;
		if (!eventCams) return [];
		return eventCams.map((s) => ({
			label: s.scheduleName || s.id,
			value: s.scheduleId,
		}));
	});

	const initialSectorIds = urlSectors
		? [...new Set(urlSectors.split(",").filter(Boolean))]
		: [];
	const selectedCameras = ref<string[]>([]);
	const selectedSectors = ref<string[]>(initialSectorIds);

	const buildSelectAllValues = (cams: CameraWithSchedule[]) => cams.map(c => c.scheduleId);

	// Cameras are not URL-tracked — always default to "all available" once the
	// schedule data resolves (initial load) and on event switch.
	watch(
		() => eventCameraIDsQuery?.data?.value,
		(newCamIds) => {
			if (!newCamIds || newCamIds.length === 0) {
				selectedCameras.value = [];
				return;
			}
			selectedCameras.value = buildSelectAllValues(newCamIds);
		},
	);

	// --- Core filter state ---
	const selectedDateRange = ref<[Date, Date] | null>(parseDateRange(urlDateRange) || null);
	const selectedClass = ref("person");
	const selectedGranularity = ref(urlGranularity);
	const selectedZone = ref<string>("");
	const selectedDetail = ref<string>("");
	const selectedGroupBy = ref<GroupByLevel>("cam");

	// Normalize end date to 23:59:59.999
	watch(selectedDateRange, (range) => {
		if (!range || !range[0] || !range[1]) return;

		const endHours = range[1].getHours();
		if (endHours !== 23 || range[1].getMinutes() !== 59) {
			const normalized = new Date(range[1]);
			normalized.setHours(23, 59, 59, 999);
			selectedDateRange.value = [range[0], normalized];
		}
	}, { flush: 'sync' });

	// --- Event liveness ---
	const eventIsLive = computed(() => {
		const events = eventsQuery?.data?.value;
		if (!events || !eventId.value) return false;

		const event = events.find(e => e.event_id === eventId.value);
		if (!event) return false;

		const nowMs = now.value;
		return nowMs >= (event.event_start * 1000) && nowMs < (event.event_end * 1000);
	});

	// --- Min/max date bounds ---
	const minDate = computed(() => {
		const events = eventsQuery?.data?.value;
		const event = events?.find(e => e.event_id === eventId.value);
		if (event) {
			const date = new Date(event.event_start * 1000);
			date.setHours(0, 0, 0, 0);
			return date;
		}
		return undefined;
	});

	const maxDate = computed(() => {
		const events = eventsQuery?.data?.value;
		const event = events?.find(e => e.event_id === eventId.value);

		if (event) {
			const date = new Date(event.event_end * 1000);
			date.setHours(23, 59, 59, 999);

			if (date.getTime() > todayMidnight.value.getTime()) {
				return todayMidnight.value;
			}
			return date;
		}
		return todayMidnight.value;
	});

	// --- Event ID watcher: handles both initial load and event switches ---
	watch(eventId, (newEventId, oldEventId) => {
		if (newEventId === oldEventId) return;

		// --- Initial load: eventId resolved from undefined → value ---
		if (!initialLoadComplete) {
			initialLoadComplete = true;

			if (!newEventId) return;

			// Fill in defaults ONLY for params that weren't in the URL
			const events = eventsQuery?.data?.value;
			const event = events?.find(e => e.event_id === newEventId);

			if (event) {
				// Date range: only compute default if not provided in URL
				if (!urlDateRange) {
					selectedDateRange.value = computeEventDateRange(event);

					const [start, end] = selectedDateRange.value;
					const startSeconds = Math.floor(start.getTime() / 1000);
					const endSeconds = Math.floor(end.getTime() / 1000);
					selectedGranularity.value = findSmallestValidGranularity(startSeconds, endSeconds);
				}
				// else: selectedDateRange & selectedGranularity already from URL
			}

			// Cameras are handled by the camera data watcher above
			return;
		}

		// --- Actual event switch (post initial load) ---

		// Clear cameras + sectors — their respective data watchers will
		// repopulate once new event data arrives.
		selectedCameras.value = [];
		selectedSectors.value = [];

		// Reset class filter
		selectedClass.value = "person";

		if (!newEventId) {
			// No event selected: clear date range
			selectedDateRange.value = null;
			selectedGranularity.value = "hour";
			return;
		}

		// Find the new event to compute date range and liveness
		const events = eventsQuery?.data?.value;
		const event = events?.find(e => e.event_id === newEventId);

		if (event) {
			selectedDateRange.value = computeEventDateRange(event);

			// Set granularity for the new range
			const [start, end] = selectedDateRange.value;
			const startSeconds = Math.floor(start.getTime() / 1000);
			const endSeconds = Math.floor(end.getTime() / 1000);
			selectedGranularity.value = findSmallestValidGranularity(startSeconds, endSeconds);
		} else {
			// Event data not loaded yet — will be handled once eventsQuery resolves
			selectedDateRange.value = null;
			selectedGranularity.value = "hour";
		}
	});

	// Watcher for eventsQuery data: fills in date range and granularity
	// if the eventId is set but events weren't available when eventId watcher fired
	watch(
		() => eventsQuery?.data?.value,
		(events) => {
			if (!events || events.length === 0 || !eventId.value) return;

			// Only auto-populate if date range is currently not set
			if (selectedDateRange.value !== null) return;

			// If date range was explicitly set in URL, don't override it
			if (urlDateRange) return;

			const event = events.find(e => e.event_id === eventId.value);
			if (event) {
				selectedDateRange.value = computeEventDateRange(event);

				const [start, end] = selectedDateRange.value;
				const startSeconds = Math.floor(start.getTime() / 1000);
				const endSeconds = Math.floor(end.getTime() / 1000);
				selectedGranularity.value = findSmallestValidGranularity(startSeconds, endSeconds);
			}
		}
	);


	const granularityLimits: Record<string, number> = {
		minute: 1 * 24 * 3600000,
		hour: 3 * 24 * 3600000,
		day: 60 * 24 * 3600000,
		week: 365 * 24 * 3600000,
		month: 3 * 365 * 24 * 3600000,
		year: 5 * 365 * 24 * 3600000,
	};

	watch(
		selectedGranularity,
		(granularity) => {
			if (!selectedDateRange.value || !selectedDateRange.value[0]) return;

			const limit = granularityLimits[granularity];
			if (!limit) return;

			// Expand to full event range, then clamp if it exceeds the granularity limit
			const events = eventsQuery?.data?.value;
			const event = events?.find(e => e.event_id === eventId.value);

			let rangeStart: Date;
			let rangeEnd: Date;

			if (event) {
				const eventRange = computeEventDateRange(event);
				rangeStart = eventRange[0];
				rangeEnd = eventRange[1];
				// Cap end at today
				const today = endOfDay(new Date());
				if (rangeEnd.getTime() > today.getTime()) {
					rangeEnd = today;
				}
			} else {
				rangeStart = selectedDateRange.value[0];
				rangeEnd = selectedDateRange.value[1] || selectedDateRange.value[0];
			}

			const durationMs = rangeEnd.getTime() - rangeStart.getTime();

			// Clamp if range exceeds this granularity's limit
			if (durationMs > limit) {
				const newStart = new Date(rangeEnd.getTime() - limit);
				selectedDateRange.value = [startOfDay(newStart), endOfDay(rangeEnd)];
			} else {
				selectedDateRange.value = [rangeStart, rangeEnd];
			}
		}
		, { immediate: true });

	// Note: selectedDateRange auto-adjustment removed - let granularity watcher handle clamping
	// When granularity changes, it clamps the range to fit. We shouldn't revert the granularity
	// based on range changes, only let the granularity watcher control both granularity and range.

	// --- Derived camera helpers ---
	// Passthrough — no sector grouping in filter selections
	const expandedScheduleIds = computed(() => [...new Set(selectedCameras.value)]);

	// Unique cam_ids for the currently selected scheduleIds (for DynamoDB queries)
	const selectedCamIds = computed(() => {
		const allSchedules = eventCameraIDsQuery?.data?.value ?? [];
		const selectedSet = new Set(expandedScheduleIds.value);
		const camIds = allSchedules
			.filter(s => selectedSet.has(s.scheduleId))
			.map(s => s.id);
		return [...new Set(camIds)];
	});

	const schedules = computed(() => eventCameraIDsQuery?.data?.value ?? []);

	// --- Class options ---
	const classOptions = computed(() => {
		const options: FilterOption[] = [{ label: "All Classes", value: "all" }];
		classes.value.forEach((cls) => {
			options.push({ label: cls, value: cls });
		});
		return options;
	});

	// --- Debounced URL sync (stats route only) ---
	// EventLive must stay paramless. Only the stats route persists filter
	// state into the URL — see the route-change watcher below for the scrub
	// when the user leaves stats.
	let urlUpdateTimer: ReturnType<typeof setTimeout> | null = null;

	function serializeIds(values: string[], allValues: string[]): string | undefined {
		if (values.length === 0) return undefined;
		// "All selected" → omit the key so the URL stays short and fresh share
		// links don't pin a stale subset when the project adds new sectors.
		if (allValues.length > 0 && values.length === allValues.length) {
			const set = new Set(values);
			if (allValues.every((id) => set.has(id))) return undefined;
		}
		return [...new Set(values)].sort().join(",");
	}

	watch(
		[selectedSectors, selectedDateRange, selectedGranularity, eventId],
		([sects, dr, g]) => {
			if (urlUpdateTimer) clearTimeout(urlUpdateTimer);

			urlUpdateTimer = setTimeout(() => {
				if (route.name !== URL_OWNER_ROUTE) return;

				const nextQuery: Record<string, string> = {};
				const drStr = dateRangeToUrlString(dr as [Date, Date] | null);
				if (drStr) nextQuery.date_range = drStr;
				if (g) nextQuery.granularity = g as string;

				const allSectors = (sectorsQuery?.data?.value ?? []).map((s) => s.sector_id);
				const sectsStr = serializeIds((sects as string[]) ?? [], allSectors);
				if (sectsStr) nextQuery.sectors = sectsStr;

				// Skip the navigation if nothing actually changed — vue-router
				// otherwise reports a NavigationDuplicated warning.
				const same =
					Object.keys(nextQuery).length === Object.keys(route.query).length &&
					Object.entries(nextQuery).every(([k, v]) => route.query[k] === v);
				if (same) return;

				router.replace({ name: route.name, params: route.params, query: nextQuery });
			}, URL_UPDATE_DEBOUNCE_MS);
		}
	);

	// Scrub query params when leaving the stats route. EventLive's onMount
	// scrubs as well, but doing it here covers any other future routes too.
	watch(
		() => route.name,
		(name, oldName) => {
			if (oldName === URL_OWNER_ROUTE && name !== URL_OWNER_ROUTE && Object.keys(route.query).length > 0) {
				router.replace({ name: route.name, params: route.params, query: {} });
			}
		},
	);

	return {
		selectedProjectId: projectId,
		selectedEventId: eventId,
		selectedProjectName,
		selectedEventName,
		selectedCameras,
		selectedSectors,
		selectedCamIds,
		expandedScheduleIds,
		schedules,
		selectedDateRange,
		selectedClass,
		selectedGranularity,
		selectedZone,
		selectedDetail,
		selectedGroupBy,
		classOptions,
		cameraOptions,
		eventIsLive,
		minDate,
		maxDate,
		eventsQuery,
	};
}